import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { browserHistory, Link } from 'react-router';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { SetImpactGroup, SetImpact, SetImpactName, SetProtection, SetSummary } from '../../actions';
import * as d3 from 'd3';
import RiskFinderComponent from '../RiskFinderComponent';
import styles from './ImpactsData.css';
import withStyles from '../../decorators/withStyles';
import { impactGroups, isIDB, isUS, toFixed } from "../../utils/utils";


class ImpactsData extends Component {

  static propTypes = {
  };

  static defaultProps = {
    summarySize: 5,
    impactGroupOrder: ['Population', 'Buildings', 'Infrastructure', 'Contamination Risks', 'Land'],
    hasTable: true,
    hasChoropleth: true,
  };

  constructor(props) {
    super(props);
    this.state = {
      data: {},
      dlType: "impact-summary",
      downloadOpen: false,
      settingsOpen: false,
      sourcesOpen: false,
    };

    /*
     * The following structure was derived form selection criteria in the "SSRF 2.0 Stats Logic" Google Doc,
     * and review of the SSRF 1.0 tiles_data.json structure. Default values are implicit: if 'min' is not
     * defined, it is assumed to be 1 by the algorithm, and if 'name' is not defined, it is assumed to be the fact key.
     * If a value is selected and equals 1, the singular form of the name is used, assumed to be the name minus an s
     * at the end, unless 'singular' is defined, in which case that name is used.
     */
    const placeID = () => this.props.place.id;
    this.acresToSqMiles = acres => parseFloat((Math.round(0.0015625 * acres * 10) / 10).toPrecision(2));
    this.factCriteria = {
      Property: {rank: 5, min: 20, posttransform: val => val >= 1000 ? `$${Math.round(val / 1000 * 10) / 10} Billion` : `$${val} Million`, name: "Property value", singular: null},
      Housing: {rank: 3, min: 50, name: "Homes", singular: null},
      Population: {rank: 1, singular: null},
      Population_Jamaica: {rank: 1, singular: null},
      Population_Newprovidence: {rank: 1, singular: null},
      Population_Worldpop15: {rank: 1, singular: null},
      Population_Landscan10: {rank: 1, singular: null},
      Population_highSV: {rank: 2, min: 100, name: "High social vulnerability population", singular: null},
      Airports_regional: {rank: 6, name: "Airports"},
      Powerplants: {rank: 4, name: "Power plants"},
      Hazwaste: {rank: 7, name: "Hazardous waste sites"},
      Wastewater: {rank: 8, name: "Wastewater sites"},
      Roads: {rank: 9, name: "Miles of road", posttransform: val => `${val} miles`, singular: "Mile of Road"},
      Hospitals: {rank: 10},
      Schools: {rank: 11},
      Land: {rank: 12, name: "Square miles of land",
        posttransform: val => isUS(placeID()) ? `${val} sq. miles` : `${val} sq. km`,
        pretransform: val => isUS(placeID()) ? this.acresToSqMiles(val) : val,
        min: 2},  // Acres to square miles
      Worship: {rank: 13, name: "Houses of worship", singular: "House of worship"},
      Museums: {rank: 14},
      Libraries: {rank: 15, singular: "Library"},
      Railroads: {rank: 16, name: "Miles of railroad", posttransform: val => `${val} miles`, singular: "Mile of railroad", mutex: ["Roads"]},
      EPA: {rank: 17, name: "EPA-listed sites"},
      Hazmat: {rank: 18, name: "Hazardous materials facilities", singular: "Hazardous materials facility", mutex: ["Hazwaste", "Hazmat_extreme"]},
      Hazmat_extreme: {rank: 19, name: "Extreme hazmat facilities", singular: "Extreme hazmat facility"},
      Hazmat_oil: {rank: 20, name: "Oil facilities", singular: "Oil facility", mutex: ["EPA_oil"]},
      Hazmat_pesticides: {rank: 21, name: "Pesticide facilities", singular: "Pesticide facility"},
      Wastewater_treatment: {rank: 22, name: "Sewage plants", mutex: ["Wastewater"]},
      Gov: {rank: 23, name: "Government buildings"},
      Schools_collu: {rank: 24, name: "Colleges and universities", singular: "College", min: 2},
      Assisted: {rank: 25, name: "Assisted living facilities", singular: "Assisted living facility"},
      EPA_oil: {rank: 26, name: "EPA OIL sites", mutex: ["Hazmat_oil"]},
      TV: {rank: 27, name: "TV transmitter sites"},
      Radio_fm: {rank: 28, name: "FM radio transmitter sites"},
      Roads_local: {rank: 29, name: "Local roads", posttransform: val => `${val} miles`},
      Roads_primary: {rank: 30, name: "Primary roads", posttransform: val => `${val} miles`},
      Roads_secondary: {rank: 30, name: "Secondary roads", posttransform: val => `${val} miles`},
      Roads_federal: {rank: 31, name: "Federal roads", posttransform: val => `${val} miles`},
      Roads_state: {rank: 32, name: "State roads", posttransform: val => `${val} miles`},
      Roads_county: {rank: 33, name: "County roads", posttransform: val => `${val} miles`},
      Rail_passenger: {rank: 34, name: "Passenger rail", posttransform: val => `${val} miles`},
      Rail_amtrak: {rank: 35, name: "Amtrak rail", posttransform: val => `${val} miles`},
      Rail_transit: {rank: 36, name: "Transit rail", posttransform: val => `${val} miles`},
      Rail_mainline: {rank: 37, name: "Mainline rail", posttransform: val => `${val} miles`},
      Rail_nonmainline: {rank: 38, name: "Non-mainline rail", posttransform: val => `${val} miles`},
      Protected: {rank: 39, name: "Protected land", pretransform: this.acresToSqMiles, posttransform: val => `${val} sq. miles`},
      Protected_fed: {rank: 40, name: "Federal protected land", pretransform: this.acresToSqMiles, posttransform: val => `${val} sq. miles`},
      Protected_state: {rank: 41, name: "State protected land", pretransform: this.acresToSqMiles, posttransform: val => `${val} sq. miles`},
      Protected_local: {rank: 42, name: "Local protected land", pretransform: this.acresToSqMiles, posttransform: val => `${val} sq. miles`},
      Protected_NPS: {rank: 43, name: "National Park Service land", pretransform: this.acresToSqMiles, posttransform: val => `${val} sq. miles`},
      Protected_FWS: {rank: 44, name: "Fish & Wildlife Service land", pretransform: this.acresToSqMiles, posttransform: val => `${val} sq. miles`}
    };
  }

  setProtection(impactsData) {
    const hasTidelcontiglevees = Object.keys(impactsData.tidelcontiglevees).length > 0;
    const hasTidelthresh = Object.keys(impactsData.tidelthresh).length > 0;
    if (hasTidelcontiglevees && !hasTidelthresh && this.props.protection !== 'protected') {
      this.props.dispatch(SetProtection('protected'));
    } else if (hasTidelthresh && !hasTidelcontiglevees && this.props.protection !== 'unprotected') {
      this.props.dispatch(SetProtection('unprotected'));
    }
  }

  async loadData(callback) {
    var self = this;
    const { place } = this.props;
    if (!place.id) {
      return;
    }

    var slrdb = "?slrdb=true";
    if (place.id.endsWith(".us")) {
      slrdb = "";
    }

    const urls = [
      '/api/impacts/' + place.type + '/' + place.id + '/self' + slrdb,
      '/api/metadata/impacts/' + place.type + '/' + place.id,
    ];

    const results = await Promise.all(urls.map((url) => d3.json(url)));
    const [data, metadata] = results;
    const cb = () => {
      this.setValidImpact(this.getImpactsByGroup(data), () => {
        try {
          this.props.dispatch(SetImpactName(this.getLocalImpacts(data)[this.props.impact].name));
        } catch {
          console.error(`Failed to set impact name for ${this.props.impact}`);
        }
        this.summarizeData();
        callback && callback();
      });
    };
    this.setProtection(data);
    this.setState({data: {impacts: data, metadata: self.indexMetadata(metadata)}}, cb);
  }

  componentDidMount() {
    // super.componentDidMount();
    this.loadData();
  }

  componentDidUpdate(props) {
    if (props.place.id !== this.props.place.id || props.place.type !== this.props.place.type) {
      this.loadData();
    }
    if (props.waterLevel.level != this.props.waterLevel.level ||
      props.waterLevel.unit !== this.props.waterLevel.unit ||
      props.protection !== this.props.protection) {
      this.update();
    }
  }

  hasData() {
    return this.state && this.state.data && Object.keys(this.state.data).length > 0;
  }

  update() {
    if (this.hasData()) {
      this.summarizeData();
    }
  }

  getDigits(num) {
    return parseInt(num.toExponential().match(/\+([0-9]+)/)[1])
  }

  round(value) {
    if (this.props.percentage) {
      return x => x;
    } else {
      try {
        const maxDigits = this.getDigits(value);
        return x => {
          try {
            if (this.getDigits(x) < maxDigits) {
              return parseFloat(x.toPrecision(1));
            } else {
              return parseFloat(x.toPrecision(2));
            }
          } catch (err) {
            return x;
          }
        }
      } catch (err) {
        return x => x;
      }
    }
  }

  summarizeData() {
    var impacts = this.state.data.impacts;
    var summary = [];
    var protection = this.props.protection === "protected" ? "tidelcontiglevees" : "tidelthresh";
    var thisPlaceImpacts = Object.values(impacts[protection])[0];

    var factKeys = Object.keys(this.factCriteria);
    factKeys.sort((a, b) =>{
      // Use defined preference of Population keys to break ties in choosing
      // a population metric, before using rank, as Population variables are all given
      // the same rank to ensure they're treated the same in their ordering with other variables.
      const keyPriority = impactGroups.Population;
      if (keyPriority.indexOf(a) < keyPriority.indexOf(b)) {
        return -1;
      } else if (this.factCriteria[a].rank < this.factCriteria[b].rank) {
        return -1;
      } else {
        return 1;
      }
    });

    var seen = {};
    var i = 0;
    var j = 0;
    while (i < this.props.summarySize && j < factKeys.length) {
      if (!thisPlaceImpacts || !(factKeys[j] in thisPlaceImpacts)) {
        j++;
        continue;
      }
      var criterion = this.factCriteria[factKeys[j]];
      if ("mutex" in criterion && criterion["mutex"] in seen) {
        j++;
        continue;
      }
      try {
        var valAtWaterLevel = thisPlaceImpacts[factKeys[j]].water_level_values[this.props.waterLevel.unit][this.props.waterLevel.level];
      } catch (err) {
        valAtWaterLevel = 0;
      }
      valAtWaterLevel = this.round(valAtWaterLevel)(valAtWaterLevel);
      if ("pretransform" in criterion) {
        valAtWaterLevel = criterion["pretransform"](valAtWaterLevel);
      }
      if ("min" in criterion) {
        if (valAtWaterLevel >= criterion["min"] || Object.keys(thisPlaceImpacts).length <= this.props.summarySize) {
          if ("posttransform" in criterion) {
            valAtWaterLevel = criterion["posttransform"](valAtWaterLevel);
          }
          summary.push({name: criterion.name || factKeys[j], value: valAtWaterLevel});
          seen[factKeys[j]] = true;
          i++;
        }
      } else if (valAtWaterLevel > 0) {
        if ("posttransform" in criterion) {
          valAtWaterLevel = criterion["posttransform"](valAtWaterLevel);
        }
        summary.push({name: criterion.name || factKeys[j], value: valAtWaterLevel});
        seen[factKeys[j]] = true;
        i++;
      }
      j++;
    }

    this.props.dispatch(SetSummary(1, {type: "impacts", data: summary}));
  }

  getLocalImpacts(impacts) {
    const contiglevees = this.props.protection === "protected" ? "tidelcontiglevees" : "tidelthresh";
    const thisplace = Object.keys(impacts[contiglevees])[0];

    return impacts[contiglevees][thisplace];
  }

  getImpactsByGroup(impacts) {
    var impactsByGroup = {};
    this.props.impactGroupOrder.forEach(k => {impactsByGroup[k] = []});
    if (Object.keys(impacts).length === 0) {
      return impactsByGroup;
    }

    const localImpacts = this.getLocalImpacts(impacts);

    // Remove exposure keys which represent the same type of variable, if multiple are available.
    // keyPriority determines the order of preference in selecting a Population key to remain.
    // As of 11/27/2017, Population is the only exposure type with overlapping data available.
    // Others are possible, and logic to handle them will need to be added here.
    const keyPriority = impactGroups.Population;
    const prioritySet = new Set(keyPriority);
    const sorted = Object.keys(localImpacts).sort((a, b) => keyPriority.indexOf(a) < keyPriority.indexOf(b) ? -1 : 1);
    const dedupedKeys = new Set();

    for (var i=0; i < sorted.length; i++) {
      var key = sorted[i];
      if (!dedupedKeys.has(key)) {
        dedupedKeys.add(key);
        if (prioritySet.has(key)) {
          break
        }
      }
    }

    dedupedKeys.forEach(key => {
      if (localImpacts.hasOwnProperty(key)) {
        var item = localImpacts[key];
        item['key'] = key;
        item.groups.forEach(group => {
          impactsByGroup[group[0]].push(item);
        });
      }
    });

    return impactsByGroup;
  }

  setValidImpact(impactsByGroup, callback) {
    const impactGroup = this.props.impactGroup;
    const isValid = (
      impactGroup in impactsByGroup &&
      impactsByGroup[impactGroup]
        .map(impact => impact.key)
        .some(k => k === this.props.impact)
    );
    const validGroups = Object.keys(impactsByGroup).filter(k => impactsByGroup[k].length > 0);

    if (!isValid && validGroups.length > 0) {
      const newGroup = validGroups[0];
      this.props.dispatch(SetImpactGroup(newGroup));
      this.props.dispatch(SetImpact(impactsByGroup[newGroup][0].key));
      this.props.dispatch(SetImpactName(impactsByGroup[newGroup][0].name));
    }

    callback && callback();
  }

  indexMetadataCatGeo(meta) {
    const idx = {categories: {}, geography: {}};
    meta.categories.forEach(obj1 => {
      Object.values(obj1).forEach((obj2) => {
        obj2.forEach(obj3 => {
          var values = Object.values(obj3);
          if (values[0].constructor === Array) {
            values = values[0];
          }
          values.forEach(obj4 => {
            var data;
            if (Object.keys(obj4).length === 1) {
              data = Object.values(obj4)[0];
            } else {
              data = obj4;
            }
            idx.categories[data.category_id] = data;
          });
        });
      })
    });

    meta.geography.ptypes.forEach(obj1 => {
      idx.geography[obj1.placetype] = obj1;
    });

    return idx;
  }

  indexMetadataSimplified(meta) {
    const idx = {categories: {}, geography: {}, simplified: true};
    // TODO: build metadata.
    return idx;
  }

  indexMetadata(meta) {
    if (meta.simplified === true) {
      return this.indexMetadataSimplified(meta);
    } else {
      return this.indexMetadataCatGeo(meta);
    }
  }

  elevMeta(placeID) {
    if (isUS(placeID)) {
      return {
        name: 'lidar',
        url: 'https://coast.noaa.gov/digitalcoast/data/coastallidar.html'
      }
    } else if (isIDB(placeID)) {
      return {
        name: 'Climate Central\u0027s CoastalDEM\u2122',      // apostrophe and TM symbol, unicode
        url: 'http://sealevel.climatecentral.org/uploads/research/CoastalDEMv1.1-Brief.pdf'
      }
    } else {
      return {
        name: 'NASA SRTM v3',
        url: 'https://earthdata.nasa.gov/nasa-shuttle-radar-topography-mission-srtm-version-3-0-global-1-arc-second-data-released-over-asia-and-australia'
      }
    }
  }

  boundaryMetaCatGeo(metadata, comparisons, comparisonType) {
    const legacyPtypePair = comparisons.find(pair => pair[0] === comparisonType);
    if (legacyPtypePair && legacyPtypePair[1] in metadata.geography) {
      const meta = metadata.geography[legacyPtypePair[1]];
      return meta.boundary_source;
    } else {
      return {
        "name": "Error: source not available",
        "mouseover": "",
        "url": ""
      }
    }
  }

  boundaryMetaSimplified() {
    // TODO FIXME: don't use hardcoded value
    return {
      name: "GADM 2.0",
      mouseover: "Global Administrative Areas 2.0",
      url: "http://www.gadm.org/"
    }
  }

  boundaryMeta(metadata, comparisons, comparisonType) {
    if (this.state.data.metadata.simplified === true) {
      // FIXME: Quick hack to provide source for Ecuador heritage cities
      if (comparisonType == 'heritage-city') {
        return {
          name: 'Sistema Nacional De Cultura, Ecuador',
          mouseover: 'Sistema Nacional De Cultura, Ecuador',
          url: 'https://www.culturaypatrimonio.gob.ec/sistema-nacional-de-cultura/',
        }
      } else {
        return this.boundaryMetaSimplified();  
      }
    } else {
      return this.boundaryMetaCatGeo(metadata, comparisons, comparisonType);
    }
  }

  /** FIXME
   * This is bad.
   * All of this should come from the database. So should anything coming from the catGeo methods.
   */
  impactMetaSimplified(data, impact) {
    if (impact === "Population" || impact == "Population_Landscan10") {
      return {
        method: "Tally of population living on land lower than the selected water level, " +
        "based on population densities from LandScan 2010. For density calculations, " +
        "population is assumed to be evenly distributed " +
        "across the land within each LandScan cell.",
        sources: {
          names: ["Landscan"],
          mouseovers: ["Landscan 2010"],
          urls: ["http://web.ornl.gov/sci/landscan/"]
        }
      }
    } else if (impact === "Population_Worldpop15") {
        return {
          method: "Tally of population living on land lower than the selected water level, " +
          "based on population densities from Worldpop 2015. For density calculations, " +
          "population is assumed to be evenly distributed " +
          "across the land within each Worldpop cell.",
          sources: {
            names: ["Worldpop"],
            mouseovers: ["Worldpop 2015"],
            urls: ["http://www.worldpop.org.uk"]
          }
        }
    } else if (impact === "Population_Jamaica") {
      return {
        method: "Tally of population living on land lower than the selected water level, " +
        "based on population densities from Jamaica Census. For density calculations, " +
        "population is assumed to be evenly distributed " +
        "across the land within each census block.",
        sources: {
          names: ["Jamaica Census"],
          mouseovers: ["Jamaica Census"],
          urls: ["http://statinja.gov.jm"]
        }
      }
    } else if (impact === "Population_Jamaica_above_poverty") {
      return {
        method: "Tally of population above poverty level (defined as 3.2USD per day) " +
        "living on land lower than the selected water level, " +
        "based on population densities from Jamaica Census. For density calculations, " +
        "population is assumed to be evenly distributed " +
        "across the land within each census block.",
        sources: {
          names: ["Jamaica Census"],
          mouseovers: ["Jamaica Census"],
          urls: ["http://statinja.gov.jm"]
        }
      }
    } else if (impact === "Population_Jamaica_below_poverty") {
      return {
        method: "Tally of population below poverty level (defined as 3.2USD per day) " +
        "living on land lower than the selected water level, " +
        "based on population densities from Jamaica Census. For density calculations, " +
        "population is assumed to be evenly distributed " +
        "across the land within each census block.",
        sources: {
          names: ["Jamaica Census"],
          mouseovers: ["Jamaica Census"],
          urls: ["http://statinja.gov.jm"]
        }
      }
    } else if (impact === "Population_Newprovidence") {
      return {
        method: "Tally of population " +
        "living on land lower than the selected water level, " +
        "based on population densities from Bahamas Census. For density calculations, " +
        "population is assumed to be evenly distributed " +
        "across the land within each census block.",
        sources: {
          names: ["Bahamas Census"],
          mouseovers: ["Bahamas Census"],
          urls: ["https://www.bahamas.gov.bs/wps/portal/public/Key%20Statistics/Social%20Statistics/!ut/p/b1/vZDJkqJAEIafpR_ApoCywGOxLxbIvlwIcQVlEwT06adnOcylx8uEmaeM-DK-zJ9KqZhK6-1YnLZD0dTb6885RZkjAMdbr1jMSzIEGEHMCUuXVSGiIioOEzBLUJ8U3c_Ey6i1J15uzY2AVJHgmfTtzRsj1cpxwVdZ3edWt-UWC-TJkCR7U0zjzqh32mpXV4CcdTm5m-56sn3bNu_K0TTSQblqxTCbfseT26jmVs3lbCayiOua-njrL_J57Jc3B9pd1YvxUNqndRQI9wKmUpluYyU83Z-OBguy97alFQ07RslXqN5k8HZuTh9fTyZfT4JvCoNXGfze_wYACvM6w_QXwgKVYAx5ovohADoNsOvRDrBZ5g_wrxP_JQEK_QpgqOQL4L49k0eUT8UAZl75aPXn5emWT-9hSfrkS_YDSMHkAda3fJn2LJv2JZkBMm1a_mH25aVBynBNVv5mH7qBgEVanDfhKyH3ZqEN3y2k3y18d6Tw_0dqUGmRV5_TrvoEn5CHkOFpFqwYjlvyPBWWAeKkXp_kzT1cn-s2jVJJF06YxI4Rh-K2rbrLaDg7_ziyNWmD3pfSvmBz3cZLvU9WED7ioLr597BR5qzsb2RB5tlFc3035PbQqOFRaLJE54K2EogGs1hZaDmPIEymQ8D2rWkRJBfDdMU0reKHeomu16fgzh277GjIxGc7uMRJOcV8XSyWD02LiutTvlyNfGLDqkR6CtR90-z2U7qZHIehA2eDhlO7Gs-YG6JR4D8oS2uqA9VWo7lGrny0_m748QPaQ3al/dl4/d5/L2dBISEvZ0FBIS9nQSEh/?1dmy&urile=wcm%3apath%3a/mof_content/internet/department+of+statistics/government/key+statistics/social+statistics/population+and+census"]
        }
      }
    } else if (impact === "Housing_Newprovidence") {
      return {
        method: "Tally of dwelling units " +
        "on land lower than the selected water level, " +
        "based on district values from Bahamas Census. For density calculations, " +
        "dwelling units are assumed to be evenly distributed " +
        "across the land within each census district.",
        sources: {
          names: ["Bahamas Census"],
          mouseovers: ["Bahamas Census"],
          urls: ["https://www.bahamas.gov.bs/wps/portal/public/Key%20Statistics/Social%20Statistics/!ut/p/b1/vZDJkqJAEIafpR_ApoCywGOxLxbIvlwIcQVlEwT06adnOcylx8uEmaeM-DK-zJ9KqZhK6-1YnLZD0dTb6885RZkjAMdbr1jMSzIEGEHMCUuXVSGiIioOEzBLUJ8U3c_Ey6i1J15uzY2AVJHgmfTtzRsj1cpxwVdZ3edWt-UWC-TJkCR7U0zjzqh32mpXV4CcdTm5m-56sn3bNu_K0TTSQblqxTCbfseT26jmVs3lbCayiOua-njrL_J57Jc3B9pd1YvxUNqndRQI9wKmUpluYyU83Z-OBguy97alFQ07RslXqN5k8HZuTh9fTyZfT4JvCoNXGfze_wYACvM6w_QXwgKVYAx5ovohADoNsOvRDrBZ5g_wrxP_JQEK_QpgqOQL4L49k0eUT8UAZl75aPXn5emWT-9hSfrkS_YDSMHkAda3fJn2LJv2JZkBMm1a_mH25aVBynBNVv5mH7qBgEVanDfhKyH3ZqEN3y2k3y18d6Tw_0dqUGmRV5_TrvoEn5CHkOFpFqwYjlvyPBWWAeKkXp_kzT1cn-s2jVJJF06YxI4Rh-K2rbrLaDg7_ziyNWmD3pfSvmBz3cZLvU9WED7ioLr597BR5qzsb2RB5tlFc3035PbQqOFRaLJE54K2EogGs1hZaDmPIEymQ8D2rWkRJBfDdMU0reKHeomu16fgzh277GjIxGc7uMRJOcV8XSyWD02LiutTvlyNfGLDqkR6CtR90-z2U7qZHIehA2eDhlO7Gs-YG6JR4D8oS2uqA9VWo7lGrny0_m748QPaQ3al/dl4/d5/L2dBISEvZ0FBIS9nQSEh/?1dmy&urile=wcm%3apath%3a/mof_content/internet/department+of+statistics/government/key+statistics/social+statistics/population+and+census"]
        }
      }
    } else if (impact === "Housing_occupied_Newprovidence") {
      return {
        method: "Tally of occupied dwelling units " +
        "on land lower than the selected water level, " +
        "based on district values from Bahamas Census. For density calculations, " +
        "dwelling units are assumed to be evenly distributed " +
        "across the land within each census district.",
        sources: {
          names: ["Bahamas Census"],
          mouseovers: ["Bahamas Census"],
          urls: ["https://www.bahamas.gov.bs/wps/portal/public/Key%20Statistics/Social%20Statistics/!ut/p/b1/vZDJkqJAEIafpR_ApoCywGOxLxbIvlwIcQVlEwT06adnOcylx8uEmaeM-DK-zJ9KqZhK6-1YnLZD0dTb6885RZkjAMdbr1jMSzIEGEHMCUuXVSGiIioOEzBLUJ8U3c_Ey6i1J15uzY2AVJHgmfTtzRsj1cpxwVdZ3edWt-UWC-TJkCR7U0zjzqh32mpXV4CcdTm5m-56sn3bNu_K0TTSQblqxTCbfseT26jmVs3lbCayiOua-njrL_J57Jc3B9pd1YvxUNqndRQI9wKmUpluYyU83Z-OBguy97alFQ07RslXqN5k8HZuTh9fTyZfT4JvCoNXGfze_wYACvM6w_QXwgKVYAx5ovohADoNsOvRDrBZ5g_wrxP_JQEK_QpgqOQL4L49k0eUT8UAZl75aPXn5emWT-9hSfrkS_YDSMHkAda3fJn2LJv2JZkBMm1a_mH25aVBynBNVv5mH7qBgEVanDfhKyH3ZqEN3y2k3y18d6Tw_0dqUGmRV5_TrvoEn5CHkOFpFqwYjlvyPBWWAeKkXp_kzT1cn-s2jVJJF06YxI4Rh-K2rbrLaDg7_ziyNWmD3pfSvmBz3cZLvU9WED7ioLr597BR5qzsb2RB5tlFc3035PbQqOFRaLJE54K2EogGs1hZaDmPIEymQ8D2rWkRJBfDdMU0reKHeomu16fgzh277GjIxGc7uMRJOcV8XSyWD02LiutTvlyNfGLDqkR6CtR90-z2U7qZHIehA2eDhlO7Gs-YG6JR4D8oS2uqA9VWo7lGrny0_m748QPaQ3al/dl4/d5/L2dBISEvZ0FBIS9nQSEh/?1dmy&urile=wcm%3apath%3a/mof_content/internet/department+of+statistics/government/key+statistics/social+statistics/population+and+census"]
        }
      }
    } else if (impact === "Housing_unoccupied_Newprovidence") {
      return {
        method: "Tally of vacant dwelling units " +
        "on land lower than the selected water level, " +
        "based on district values from Bahamas Census. For density calculations, " +
        "dwelling units are assumed to be evenly distributed " +
        "across the land within each census district.",
        sources: {
          names: ["Bahamas Census"],
          mouseovers: ["Bahamas Census"],
          urls: ["https://www.bahamas.gov.bs/wps/portal/public/Key%20Statistics/Social%20Statistics/!ut/p/b1/vZDJkqJAEIafpR_ApoCywGOxLxbIvlwIcQVlEwT06adnOcylx8uEmaeM-DK-zJ9KqZhK6-1YnLZD0dTb6885RZkjAMdbr1jMSzIEGEHMCUuXVSGiIioOEzBLUJ8U3c_Ey6i1J15uzY2AVJHgmfTtzRsj1cpxwVdZ3edWt-UWC-TJkCR7U0zjzqh32mpXV4CcdTm5m-56sn3bNu_K0TTSQblqxTCbfseT26jmVs3lbCayiOua-njrL_J57Jc3B9pd1YvxUNqndRQI9wKmUpluYyU83Z-OBguy97alFQ07RslXqN5k8HZuTh9fTyZfT4JvCoNXGfze_wYACvM6w_QXwgKVYAx5ovohADoNsOvRDrBZ5g_wrxP_JQEK_QpgqOQL4L49k0eUT8UAZl75aPXn5emWT-9hSfrkS_YDSMHkAda3fJn2LJv2JZkBMm1a_mH25aVBynBNVv5mH7qBgEVanDfhKyH3ZqEN3y2k3y18d6Tw_0dqUGmRV5_TrvoEn5CHkOFpFqwYjlvyPBWWAeKkXp_kzT1cn-s2jVJJF06YxI4Rh-K2rbrLaDg7_ziyNWmD3pfSvmBz3cZLvU9WED7ioLr597BR5qzsb2RB5tlFc3035PbQqOFRaLJE54K2EogGs1hZaDmPIEymQ8D2rWkRJBfDdMU0reKHeomu16fgzh277GjIxGc7uMRJOcV8XSyWD02LiutTvlyNfGLDqkR6CtR90-z2U7qZHIehA2eDhlO7Gs-YG6JR4D8oS2uqA9VWo7lGrny0_m748QPaQ3al/dl4/d5/L2dBISEvZ0FBIS9nQSEh/?1dmy&urile=wcm%3apath%3a/mof_content/internet/department+of+statistics/government/key+statistics/social+statistics/population+and+census"]
        }
      }
    } else if (impact === "WirelessAP_Skyhook") {
      return {
        method: "Tally of wireless access points on land lower than the selected water level, " +
        "based on densities provided by Skyhook Wireless.",
        sources: {
          names: ["Skyhook Wireless"],
          mouseovers: ["Skyhook Wireless"],
          urls: ["https://www.skyhookwireless.com"]
        }
      }

    } else if (impact === "Land") {
      return {
        "method": "Total land area below the selected water level. Saltwater marsh and other marine wetlands not included.",
        "sources": {
          names: ["GADM"],
          mouseovers: ["GADM 2.0"],
          urls: ["http://www.gadm.org/"]
        }
      }
    } else {
      return {
        "method": "Error: method not available",
        "sources": "Error: sources not available",
      }
    }

  }

  impactMetaCatGeo(data, impact) {
    const impactMeta = data.metadata.categories[impact];
    if (impactMeta) {
      impactMeta.method = impactMeta.method.replace("within each Census block.", "within each Census block, " +
        "outside of areas currently classified as wetlands.");
      // if (impactMeta.sources.names.length === 0 && impact === "Land") {
      //   impactMeta.sources.names.push("Census");
      //   impactMeta.sources.mouseovers.push("U.S. Census TigerLines 2012");
      //   impactMeta.sources.urls.push("https://www.census.gov/geo/maps-data/data/tiger.html#tab_2012#tab_2012");
      // }
      return impactMeta;
    } else {
      return {
        "method": "Error: method not available",
        "sources": "Error: sources not available",
      }
    }
  }

  impactMeta(data, impact) {
    if (data.metadata.simplified === true) {
      return this.impactMetaSimplified(data, impact);
    } else {
      return this.impactMetaCatGeo(data, impact);
    }
  }

  noData = (_, impact) => {
    return impact === null || (impact && this.props.impactPct && impact.coverage_flag === 1);
  };

  render() {
    if (!this.hasData()) {
      return null;
    }

    const { impact, availableComparisons, comparisonType } = this.props;

    return (
      <div className="Impacts">
        <div>{React.Children.map(this.props.children,
          child => React.cloneElement(child, {
            data: this.state.data,
            factCriteria: this.factCriteria,
            impactMeta: this.impactMeta(this.state.data, impact),
            boundaryMeta: this.boundaryMeta(this.state.data.metadata, availableComparisons, comparisonType),
            elevMeta: this.elevMeta(this.props.place.id),
            impactsByGroup: this.getImpactsByGroup(this.state.data.impacts),
            setValidImpact: this.setValidImpact,
            noData: this.noData,
            ...this.props
          }))}
        </div>
      </div>
    );

  };
}

export default compose(
  withStyles(styles),
  connect((state) => state),
)(ImpactsData);
